/**
 * Chutt... - Design System Colors (Luxury Ivoire & Emeraude)
 * Palette centralisee — Refonte UX 2026
 *
 * Usage:
 *   import { colors } from '../styles/colors';
 *   <Box sx={{ bgcolor: colors.ivory, color: colors.charcoal }} />
 */

export const colors = {
  // === Brand Primary — Emerald ===
  primary: '#1B5E40',          // emerald deep (main)
  primaryLight: '#2E9E6B',     // emerald clair (accents, legacy compat)
  primaryBright: '#34D399',    // emerald bright (glow effects)
  primaryDark: '#14472F',      // emerald very dark
  primaryBg: 'rgba(27, 94, 64, 0.08)',

  // === Ivory backgrounds ===
  ivory: '#FAF7F2',            // fond principal
  ivoryWarm: '#F3EDE3',        // fond secondaire (Agir)
  ivoryDeep: '#E8DFD0',        // fond tertiaire (footer)

  // === Text & Dark tones ===
  charcoal: '#1A1A18',         // texte fort + fond Comprendre
  text: '#2C2C28',             // texte standard
  textMed: '#6B6860',          // texte secondaire
  textLight: '#9B9790',        // texte discret
  slate: '#2D3532',

  // === Borders (warm-toned) ===
  border: '#DDD5C8',
  borderLight: '#EDE7DC',

  // === Glass effects ===
  cardGlass: 'rgba(255,255,255,0.65)',
  cardGlassDark: 'rgba(255,255,255,0.08)',
  emeraldGlass: 'rgba(27,94,64,0.04)',

  // === Legacy gray scale (kept for backward compat) ===
  gray700: '#2D3532',
  gray600: '#4A5752',
  gray500: '#6B7A74',
  gray400: '#9BA8A2',
  gray300: '#C4CDC9',
  gray200: '#E2E8E5',
  gray100: '#F1F4F3',
  gray50: '#FAF7F2',  // mapped to ivory
  offWhite: '#FAF7F2',
  white: '#FFFFFF',

  // Legacy aliases (for existing components that reference these)
  accent: '#1B5E40',
  accentDark: '#14472F',
  accentLight: '#2E9E6B',
  accentMuted: '#A7DFC8',
  accentBg: 'rgba(27, 94, 64, 0.08)',
  mint: '#E8F5F0',
  teal: '#1B5E40',
  green: '#1B5E40',
  greenDark: '#14472F',
  greenLight: '#2E9E6B',
  greenPastel: '#E8F5F0',

  // === Semantic Colors ===
  positive: '#22C55E',
  positiveBg: 'rgba(34, 197, 94, 0.1)',

  negative: '#EF4444',
  negativePastel: '#FEF2F2',
  negativeBg: 'rgba(239, 68, 68, 0.1)',

  neutral: '#9BA8A2',
  neutralBg: 'rgba(155, 168, 162, 0.1)',

  success: '#22C55E',
  successLight: '#4ADE80',
  successDark: '#16A34A',
  successBg: 'rgba(34, 197, 94, 0.1)',

  warning: '#F59E0B',
  warningLight: '#FBBF24',
  warningDark: '#D97706',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  warningPastel: '#FDF6E3',

  danger: '#EF4444',
  dangerLight: '#F87171',
  dangerDark: '#DC2626',
  dangerBg: 'rgba(239, 68, 68, 0.1)',

  info: '#3B82F6',
  infoLight: '#60A5FA',
  infoDark: '#2563EB',
  infoBg: 'rgba(59, 130, 246, 0.1)',

  // === Supplementary ===
  blue: '#2B4C7E',
  indigo: '#4F46E5',

  // === Crisis & Priority tokens ===
  bordeaux: '#8B1A1A',
  bordeauxLight: '#A52020',
  bordeauxBg: '#F8F4EE',
  gold: '#B8860B',
  priorityUrgent: '#B91C1C',
  priorityHigh: '#B45309',

  // === Extended palette (charts, tags, categories) ===
  extended: {
    bleu: '#2B4C7E',
    indigo: '#4F46E5',
    violet: '#7C3AED',
    ocre: '#D4A843',
    orange: '#E85D3B',
    rose: '#FF69B4',
    ardoise: '#475569',
    vert: '#1B5E40',
  },
};

// Surface tokens (semantic backgrounds)
export const surfaces = {
  page: colors.ivory,
  card: 'rgba(255,255,255,0.65)',
  cardHover: 'rgba(255,255,255,0.8)',
  cardActive: colors.ivoryWarm,
  elevated: '#FFFFFF',
  overlay: 'rgba(26, 26, 24, 0.6)',
  subtle: colors.ivoryWarm,
  mint: '#E8F5F0',
};

// Gradients
export const gradients = {
  greenFade: 'linear-gradient(180deg, rgba(27, 94, 64, 0.20) 0%, rgba(27, 94, 64, 0.02) 100%)',
  tealFade: 'linear-gradient(180deg, rgba(27, 94, 64, 0.20) 0%, rgba(27, 94, 64, 0.02) 100%)',
  positiveFade: 'linear-gradient(180deg, rgba(34, 197, 94, 0.20) 0%, rgba(34, 197, 94, 0.02) 100%)',
  negativeFade: 'linear-gradient(180deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.02) 100%)',
  charcoalFade: 'linear-gradient(180deg, rgba(26, 26, 24, 0.08) 0%, rgba(26, 26, 24, 0.01) 100%)',
  scoreBg: 'radial-gradient(circle at 50% 80%, rgba(27, 94, 64, 0.06) 0%, transparent 70%)',
  heroOverlay: 'linear-gradient(135deg, rgba(27,94,64,0.85) 0%, rgba(26,26,24,0.9) 100%)',
  emeraldToCharcoal: 'linear-gradient(135deg, #1B5E40 0%, #1A1A18 100%)',
};

// Sentiment colors (reputation analysis)
export const sentimentColors = {
  positive: colors.success,
  positiveBg: colors.successBg,
  negative: colors.danger,
  negativeBg: colors.dangerBg,
  neutral: colors.gray400,
  neutralBg: 'rgba(155, 168, 162, 0.1)',
};

// Chart colors (data visualization)
export const chartColors = {
  primary: colors.primary,
  secondary: colors.slate,
  tertiary: colors.gray500,
  positive: colors.success,
  negative: colors.danger,
  neutral: colors.gray400,
  series: [
    colors.primary,
    colors.slate,
    colors.success,
    colors.warning,
    colors.info,
    colors.gray500,
  ],
};

// AI-related styling
export const aiColors = {
  border: colors.primary,
  background: colors.primaryBg,
  icon: colors.primary,
  text: colors.primary,
};

// Multi-series data visualization (Meltwater-inspired)
export const meltwaterPalette = {
  platforms: ['#F59E0B', '#8B5CF6', '#EC4899', '#1B5E40', '#3B82F6', '#6B7A74'],
  segments: ['#1B5E40', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444',
             '#22C55E', '#6366F1', '#F97316', '#2E9E6B'],
  sentiment: {
    positive: '#22C55E',
    negative: '#EF4444',
    neutral: '#9BA8A2',
  },
};

// Platform-specific colors (unchanged -- these are brand colors of external platforms)
export const platformColors = {
  twitter: '#1DA1F2',
  facebook: '#4267B2',
  linkedin: '#0077B5',
  instagram: '#E4405F',
  news: '#8B5CF6',
  forum: '#FF6B35',
  web: '#94A3B8',
  youtube: '#FF0000',
  google_reviews: '#EA4335',
};

export default colors;
